// ============================================================================
// INTELLIGENCE ENGINE V2
// Deterministic engine that converts raw data into Evidence and Recommendations
// ============================================================================

export const buildIntelligenceEngine = (summary) => {
  const evidence = [];
  const candidates = [];

  // Helper to standardise confidence rules universally
  const getConf = (volume) => {
    if (volume >= 10) return 'strong_evidence';
    if (volume >= 3) return 'emerging_pattern';
    return 'not_enough_data';
  };

  // --------------------------------------------------------------------------
  // 1. Activity & Conversion Intelligence
  // --------------------------------------------------------------------------
  const act = summary.activity || {};
  const conv = summary.conversion_rates || {};
  
  evidence.push({
    id: 'ev_activity_baseline',
    category: 'activity',
    title: 'Networking Baseline',
    summary: `Generated ${act.connections || 0} connections and ${act.outcomes_recorded || 0} outcomes.`,
    metrics: act,
    confidence: getConf(act.connections || 0),
    eligible: true,
    explanation: `Total volume of interactions across all channels during this period.`
  });

  if (conv.view_to_connection > 0) {
    evidence.push({
      id: 'ev_conversion_rate',
      category: 'conversion',
      title: 'Overall Conversion',
      summary: `You convert ${conv.view_to_connection}% of card views into actual connections.`,
      metrics: { rate: conv.view_to_connection },
      confidence: getConf(act.connections),
      eligible: true,
      explanation: `Calculated from ${act.connections} connections divided by ${act.card_views} total card views.`
    });
  }

  // --------------------------------------------------------------------------
  // 2. Event Intelligence
  // --------------------------------------------------------------------------
  const events = summary.top_events || [];
  if (events.length > 0) {
    const bestEvent = events[0];
    evidence.push({
      id: 'ev_best_event',
      category: 'event',
      title: `Top Event: ${bestEvent.event_name}`,
      summary: `Generated ${bestEvent.connections} Connections and ${bestEvent.outcomes} Outcomes.`,
      metrics: bestEvent,
      confidence: getConf(bestEvent.connections),
      eligible: true,
      explanation: `Highest performing event by connection volume out of ${events.length} active events.`
    });

    if (bestEvent.connections >= 3) {
      candidates.push({
        priority: 4,
        type: 'event_scale',
        reason: 'high_conversion',
        supported_by: ['ev_best_event'],
        title: 'Double Down on Event Success',
        summary: `Your strategy at ${bestEvent.event_name} worked perfectly. Prioritize attending similar events to scale your network.`
      });
    }

    if (events.length > 1) {
      const weakestEvent = events[events.length - 1];
      if (weakestEvent.connections === 0 && weakestEvent.views > 10) {
        evidence.push({
          id: 'ev_weakest_event',
          category: 'event',
          title: `Low Conversion Event: ${weakestEvent.event_name}`,
          summary: `High visibility but 0 connections generated.`,
          metrics: weakestEvent,
          confidence: getConf(weakestEvent.views),
          eligible: true,
          explanation: `Identified as the lowest converting event relative to traffic.`
        });
      }
    }
  }

  // --------------------------------------------------------------------------
  // 3. Card Intelligence
  // --------------------------------------------------------------------------
  const cards = summary.top_cards || [];
  if (cards.length > 0) {
    const bestCard = cards[0];
    evidence.push({
      id: 'ev_best_card',
      category: 'card',
      title: `Top Card: ${bestCard.card_name}`,
      summary: `Converted at ${bestCard.conversion_rate}% creating ${bestCard.connections} connections.`,
      metrics: bestCard,
      confidence: getConf(bestCard.connections),
      eligible: true,
      explanation: `Highest converting digital card out of ${cards.length} active cards.`
    });
  }

  // --------------------------------------------------------------------------
  // 4. QR Intelligence
  // --------------------------------------------------------------------------
  const qrs = summary.top_qr_codes || [];
  if (qrs.length > 0) {
    const bestQr = qrs[0];
    evidence.push({
      id: 'ev_best_qr',
      category: 'qr',
      title: `Top QR Code: ${bestQr.qr_label || 'Unnamed QR'}`,
      summary: `Generated ${bestQr.connections} connections from ${bestQr.scans} scans.`,
      metrics: bestQr,
      confidence: getConf(bestQr.connections),
      eligible: true,
      explanation: `Most scanned QR code driving offline-to-online connections.`
    });

    if (bestQr.connections >= 3) {
      candidates.push({
        priority: 5,
        type: 'qr_scale',
        reason: 'high_conversion',
        supported_by: ['ev_best_qr'],
        title: 'Expand QR Usage',
        summary: `Your offline QR strategy is highly effective. Place your top performing QR code in more high-traffic areas.`
      });
    }
  }

  // --------------------------------------------------------------------------
  // 5. CTA Intelligence
  // --------------------------------------------------------------------------
  const ctas = summary.top_ctas || [];
  if (ctas.length > 0) {
    const bestCta = ctas[0];
    evidence.push({
      id: 'ev_best_cta',
      category: 'cta',
      title: `Top Call-to-Action: ${bestCta.cta_label || bestCta.cta_type}`,
      summary: `Drove ${bestCta.clicks} clicks and ${bestCta.bookings} bookings.`,
      metrics: bestCta,
      confidence: getConf(bestCta.clicks),
      eligible: true,
      explanation: `Most engaging action button generating downstream events.`
    });

    if (bestCta.bookings >= 2) {
      candidates.push({
        priority: 6,
        type: 'cta_scale',
        reason: 'high_conversion',
        supported_by: ['ev_best_cta'],
        title: 'Optimize CTAs',
        summary: `Your "${bestCta.cta_label || bestCta.cta_type}" CTA is successfully driving bookings. Ensure it is prominently visible on all active cards.`
      });
    }
  }

  // --------------------------------------------------------------------------
  // 6. Source Intelligence
  // --------------------------------------------------------------------------
  const sources = summary.top_sources || [];
  if (sources.length > 0 && sources[0].source !== 'unknown') {
    const bestSource = sources[0];
    evidence.push({
      id: 'ev_best_source',
      category: 'source',
      title: `Top Source: ${bestSource.source.replace('_', ' ')}`,
      summary: `Produced ${bestSource.count} connections.`,
      metrics: bestSource,
      confidence: getConf(bestSource.count),
      eligible: true,
      explanation: `Highest volume acquisition channel.`
    });
  }

  // --------------------------------------------------------------------------
  // 7. Relationship & Opportunity Intelligence
  // --------------------------------------------------------------------------
  const rel = summary.relationships || {};
  if (rel.followups_overdue > 0) {
    evidence.push({
      id: 'ev_overdue_followups',
      category: 'relationship',
      title: 'Overdue Follow-ups',
      summary: `${rel.followups_overdue} connections are waiting for a scheduled follow-up.`,
      metrics: { overdue: rel.followups_overdue },
      confidence: 'strong_evidence',
      eligible: true,
      explanation: `Deterministic count of past-due reminders.`
    });
    
    candidates.push({
      priority: 1, // Urgent UI priority
      type: 'followup',
      reason: 'urgent_backlog',
      supported_by: ['ev_overdue_followups'],
      title: 'Clear Your Backlog',
      summary: `Prioritise your overdue connections to maximize existing opportunities before seeking new ones.`
    });
  }

  if (rel.important_without_followup > 0) {
    evidence.push({
      id: 'ev_important_neglect',
      category: 'relationship',
      title: 'Important Connections at Risk',
      summary: `${rel.important_without_followup} starred connections have no scheduled follow-up.`,
      metrics: { neglected: rel.important_without_followup },
      confidence: 'strong_evidence',
      eligible: true,
      explanation: `Count of connections marked 'important' lacking active reminders.`
    });

    candidates.push({
      priority: 2,
      type: 'nurture',
      reason: 'high_value_risk',
      supported_by: ['ev_important_neglect'],
      title: 'Nurture Key Relationships',
      summary: `Schedule follow-up tasks for your most important new connections to ensure they don't go cold.`
    });
  }

  // --------------------------------------------------------------------------
  // 8. Timing Intelligence
  // --------------------------------------------------------------------------
  const timing = summary.timing || {};
  if (timing.median_days_to_booking !== null) {
    evidence.push({
      id: 'ev_timing_booking',
      category: 'timing',
      title: 'Booking Velocity',
      summary: `It takes a median of ${timing.median_days_to_booking} days from connection to booking.`,
      metrics: timing,
      confidence: 'strong_evidence',
      eligible: true,
      explanation: `Median duration calculation across all successful bookings.`
    });
  }

  // Fallback Candidate if everything is perfect
  if (candidates.length === 0) {
    candidates.push({
      priority: 99,
      type: 'general',
      reason: 'foundation',
      supported_by: ['ev_activity_baseline'],
      title: 'Continue Building',
      summary: 'Your networking health is excellent. Keep sharing your card to generate deeper insights.'
    });
  }

  // Sort candidates by priority (1 is highest)
  candidates.sort((a, b) => a.priority - b.priority);

  return { evidence, candidates };
};

// ============================================================================
// BACKWARDS COMPATIBILITY MAPPERS
// ============================================================================

// Maps the new Evidence Objects into the {key, text} format required by ReviewSection.jsx
export const generateEvidenceContext = (engine) => {
  return engine.evidence.map(e => ({
    key: e.id,
    text: e.explanation || e.summary
  }));
};

// Robust deterministic fallback powered by the new Intelligence Engine
export const generateDeterministicReview = (summary, objective, engine) => {
  const review = {
    overall: {
      headline: "Networking Activity Summary",
      summary: `You made ${summary.activity?.connections || 0} new Connections during this period. ${summary.activity?.followups_completed || 0} received a completed follow-up and ${summary.activity?.outcomes_recorded || 0} progressed to meaningful Outcomes.`
    },
    biggest_win: null,
    what_worked: [],
    needs_attention: null,
    recommendation: null
  };

  // 1. Needs Attention (Map from Relationship Evidence)
  const attentionEv = engine.evidence.find(e => e.category === 'relationship' && (e.id.includes('overdue') || e.id.includes('neglect')));
  if (attentionEv) {
    review.needs_attention = {
      title: attentionEv.title,
      summary: attentionEv.summary,
      action_filter: attentionEv.id.includes('overdue') ? 'overdue' : 'important',
      evidence_keys: [attentionEv.id]
    };
  }

  // 2. Biggest Win (Highest positivity evidence: Event, QR, CTA, or Card)
  const winEv = engine.evidence.find(e => ['event', 'qr', 'cta', 'card'].includes(e.category) && e.confidence !== 'not_enough_data');
  if (winEv) {
    review.biggest_win = {
      title: winEv.title,
      summary: winEv.summary,
      evidence_keys: [winEv.id],
      confidence: winEv.confidence
    };
  }

  // 3. What Worked (Source or Conversion)
  const workedEv = engine.evidence.find(e => ['source', 'conversion'].includes(e.category));
  if (workedEv) {
    review.what_worked.push({
      title: workedEv.title,
      summary: workedEv.summary,
      evidence_keys: [workedEv.id],
      confidence: workedEv.confidence
    });
  }

  // Fallback if empty
  if (review.what_worked.length === 0) {
    const baseEv = engine.evidence.find(e => e.id === 'ev_activity_baseline');
    review.what_worked.push({
      title: "Foundation Building",
      summary: "You are successfully capturing interactions. More patterns will emerge as you record outcomes.",
      evidence_keys: baseEv ? [baseEv.id] : [],
      confidence: "not_enough_data"
    });
  }

  // 4. Recommendation (Strict mapping from Engine Candidates)
  const topCandidate = engine.candidates[0];
  review.recommendation = {
    title: topCandidate.title,
    summary: topCandidate.summary,
    evidence_keys: topCandidate.supported_by,
    confidence: 'strong_evidence'
  };

  return review;
};